import { WalletInlineButton } from "./WalletInlineButton";
import { WalletBatchRecipients } from "./WalletBatchRecipients";
import { WalletAmountInput } from "./WalletAmountInput";
import React from "react";
import { amountValidation } from "../helpers/amountValidation";
import type { useSendRecipient } from "../hooks/useSendRecipient";
import { BaseOperationProgress } from "./BaseOperationProgress";
import { ethers } from "ethers";
import { styles } from "../../styles";
import { EVM_CONFIG, type TokenInfo } from "../../config";
import type { BatchRecipientRow } from "../utils/batchFileParser";
import { getNftDisplayLabel, tokenOptionValue } from "../helpers/baseAppUtils";
import { parsePrivateAddress } from "../../utils/inbox-crypto";
import { isStealthHandle } from "../../utils/handle-resolver";
import { usesPrivateFees } from "../../joinsplit-v2/privateFees";
import { flatRelayFee } from "../../joinsplit-v2/relayFee";
import { useSendPreparationWarmup } from "../hooks/useSendPreparationWarmup";
import { PrivateFeeSummary } from "./PrivateFeeSummary";
import { WalletFeeSummary } from "./WalletFeeSummary";
import { formatTokenAmount } from "../helpers/amountDisplay";
import type { PrivateFeePolicy } from "../hooks/usePrivateFeePolicy";

interface BaseSendTabProps {
  /** Chain-owned validation and fees; presentation remains the shared Send form. */
  chainAdapter?: {
    recipientHint: string;
    recipientPlaceholder: string;
    validation: ReturnType<typeof amountValidation>;
    feeSummary: React.ReactNode;
    onMax: () => void;
    continueLabel?: string;
  };
  recipientCheck: ReturnType<typeof useSendRecipient>;
  canUseBatchWithdraw: boolean;
  sendTokenOptions: TokenInfo[];
  selectedSendToken: TokenInfo;
  setSelectedSendToken: (token: TokenInfo) => void;
  sendBatchMode: boolean;
  setSendBatchMode: (value: boolean) => void;
  batchFileInputRef: React.RefObject<HTMLInputElement>;
  handleBatchFileUpload: (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => void | Promise<void>;
  sendBatchRows: BatchRecipientRow[];
  setSendBatchRows: React.Dispatch<React.SetStateAction<BatchRecipientRow[]>>;
  setSendRecipient: (value: string) => void;
  setSendAmount: (value: string) => void;
  sendRecipient: string;
  sendAmount: string;
  enterBatchMode: () => void;
  activeSendToken: TokenInfo | null | undefined;
  sendPrivateBalanceDisplay: string;
  spendablePrivateBalance: bigint;
  sendMemo: string;
  setSendMemo: (value: string) => void;
  sendFeesApply: boolean;
  protocolFeeBps: number;
  privateFeePolicy?: PrivateFeePolicy | null;
  partnerFeeBps: number;
  batchSendProtocolFee: number;
  sendProtocolFee: number;
  batchSendPartnerFee: number;
  sendPartnerFee: number;
  loading: boolean;
  sendProgress?: string;
  preparationVisible?: boolean;
  connected: boolean;
  isComplianceBlocked: boolean;
  isComplianceChecking: boolean;
  onSend: () => void | Promise<void>;
  /** The relayer fee for an amount; null while a live quote is pending
   * (quoted networks). Defaults to the flat Base fee. */
  relayFee?: ((amount: bigint) => bigint) | null;
  /** Why the relayer fee is not known yet (quoted networks). */
  relayFeePending?: string | null;
}

export function BaseSendTab({
  chainAdapter,
  recipientCheck,
  canUseBatchWithdraw,
  sendTokenOptions,
  selectedSendToken,
  setSelectedSendToken,
  sendBatchMode,
  setSendBatchMode,
  batchFileInputRef,
  handleBatchFileUpload,
  sendBatchRows,
  setSendBatchRows,
  setSendRecipient,
  setSendAmount,
  sendRecipient,
  sendAmount,
  enterBatchMode,
  activeSendToken,
  sendPrivateBalanceDisplay,
  spendablePrivateBalance,
  sendMemo,
  setSendMemo,
  sendFeesApply,
  protocolFeeBps,
  privateFeePolicy,
  partnerFeeBps,
  batchSendProtocolFee,
  sendProtocolFee,
  batchSendPartnerFee,
  sendPartnerFee,
  loading,
  sendProgress,
  preparationVisible = false,
  connected,
  isComplianceBlocked,
  isComplianceChecking,
  onSend,
  relayFee = flatRelayFee,
  relayFeePending = null,
}: BaseSendTabProps) {
  useSendPreparationWarmup(
    !chainAdapter && connected && preparationVisible && usesPrivateFees(),
    loading,
  );
  const batchHasPrivateRecipients = sendBatchRows.some((row) => {
    const recipient = row.recipient.trim();
    return (
      !!recipient &&
      (Boolean(parsePrivateAddress(recipient)) || isStealthHandle(recipient))
    );
  });
  const batchHasPublicRecipients = sendBatchRows.some((row) => {
    const recipient = row.recipient.trim();
    return !!recipient && ethers.isAddress(recipient);
  });
  const batchModeIsPrivate =
    sendBatchMode && batchHasPrivateRecipients && !batchHasPublicRecipients;
  const batchModeIsPublic =
    sendBatchMode && batchHasPublicRecipients && !batchHasPrivateRecipients;
  const batchModeIsMixed =
    sendBatchMode && batchHasPrivateRecipients && batchHasPublicRecipients;

  const relayedWithdrawalForm =
    EVM_CONFIG.JOINSPLIT_V2.ENABLED &&
    !usesPrivateFees() &&
    EVM_CONFIG.JOINSPLIT_V2.RELAY_WITHDRAWALS &&
    ethers.isAddress(sendRecipient.trim());
  const validation =
    chainAdapter?.validation ??
    amountValidation({
      amount: sendAmount,
      decimals: activeSendToken?.decimals ?? selectedSendToken.decimals,
      symbol: activeSendToken?.symbol ?? selectedSendToken.symbol,
      balance: spendablePrivateBalance,
      privateBalance: true,
      policy: privateFeePolicy,
      action: ethers.isAddress(sendRecipient.trim()) ? "withdraw" : "p2p",
      deductedFee: relayedWithdrawalForm ? (relayFee ?? undefined) : undefined,
      feePending: relayedWithdrawalForm ? relayFeePending : null,
    });
  const sendSymbol = activeSendToken?.symbol ?? selectedSendToken.symbol;
  const sendDecimals = activeSendToken?.decimals ?? selectedSendToken.decimals;
  let enteredAmount: bigint | null = null;
  try {
    enteredAmount = sendAmount.trim()
      ? ethers.parseUnits(sendAmount.trim(), sendDecimals)
      : null;
  } catch {
    enteredAmount = null;
  }
  // Flat-pool relayed withdrawals take the relayer fee out of the amount.
  const withdrawalFee =
    !sendBatchMode &&
    relayedWithdrawalForm &&
    !relayFeePending &&
    relayFee &&
    enteredAmount !== null &&
    enteredAmount > 0n
      ? relayFee(enteredAmount)
      : null;
  const batchValues = sendBatchRows.map((row) =>
    amountValidation({
      amount: row.amount,
      decimals: selectedSendToken.decimals,
      symbol: selectedSendToken.symbol,
      balance: spendablePrivateBalance,
      privateBalance: true,
    }),
  );
  // Flat-pool public batches charge the relayer on top of the row amounts.
  const batchRelayFeeApplies =
    EVM_CONFIG.JOINSPLIT_V2.ENABLED &&
    batchModeIsPublic &&
    EVM_CONFIG.JOINSPLIT_V2.RELAY_WITHDRAWALS;
  const batchRelayFee = (amount: bigint) =>
    batchRelayFeeApplies && relayFee ? relayFee(amount) : 0n;
  const batchTotal = batchValues.reduce((n, v) => n + (v.value || 0n), 0n);
  const batchInvalid =
    !sendBatchRows.length ||
    batchValues.some((v) => v.error) ||
    batchTotal + batchRelayFee(batchTotal) > spendablePrivateBalance ||
    (batchRelayFeeApplies && !relayFee) ||
    batchModeIsMixed ||
    sendBatchRows.some(
      (row) =>
        !ethers.isAddress(row.recipient.trim()) &&
        !parsePrivateAddress(row.recipient.trim()) &&
        !isStealthHandle(row.recipient.trim()),
    );
  const disabled =
    !connected ||
    loading ||
    isComplianceBlocked ||
    isComplianceChecking ||
    (!chainAdapter && usesPrivateFees() && !privateFeePolicy) ||
    (sendBatchMode
      ? batchInvalid
      : !!validation.error || !recipientCheck.valid);
  return (
    <div>
      <p style={{ fontSize: "14px", color: "#b0c4de", marginBottom: "16px" }}>
        {chainAdapter?.recipientHint ??
          "Enter a recipient private address, a .stealth handle, or an EVM address to send privately or withdraw to a public wallet."}
      </p>
      {canUseBatchWithdraw && (
        <div
          style={{ fontSize: "13px", color: "#c9d8f2", marginBottom: "12px" }}
        >
          Batch sends support either P2P/private recipients or public EVM wallet
          addresses. Split mixed recipient types into separate batches.
        </div>
      )}
      <label htmlFor="wallet-send-token" style={styles.label}>
        Token
      </label>
      <select
        id="wallet-send-token"
        value={tokenOptionValue(selectedSendToken)}
        onChange={(e) => {
          const token = sendTokenOptions.find(
            (option) => tokenOptionValue(option) === e.target.value,
          );
          if (token) setSelectedSendToken(token);
        }}
        style={
          {
            ...styles.input,
            color: "#e8eeff",
            cursor: "pointer",
            appearance: "none",
            WebkitAppearance: "none",
            backgroundImage:
              'url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%2712%27 height=%278%27%3E%3Cpath d=%27M1 1l5 5 5-5%27 stroke=%27%23b0c4de%27 fill=%27none%27/%3E%3C/svg%3E")',
            backgroundRepeat: "no-repeat",
            backgroundPosition: "right 12px center",
            paddingRight: "32px",
          } as React.CSSProperties
        }
      >
        {sendTokenOptions.map((token) => (
          <option
            key={tokenOptionValue(token)}
            value={tokenOptionValue(token)}
            style={{ background: "#1a1a2e", color: "#e8eeff" }}
          >
            {token.tokenType === "erc721" ||
            token.tokenType === "erc1155" ||
            token.tokenType === "vnft"
              ? `${getNftDisplayLabel(token)} (${token.tokenType.toUpperCase()})`
              : `${token.symbol} - ${token.name}`}
          </option>
        ))}
      </select>
      {sendBatchMode ? (
        <div style={{ marginBottom: "8px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "8px",
            }}
          >
            <label style={styles.label}>Recipients</label>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <a
                href="/batch-withdraw-template.csv"
                download="batch-withdraw-template.csv"
                style={{
                  ...styles.button,
                  width: "auto",
                  padding: "6px 10px",
                  marginBottom: "6px",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  textDecoration: "none",
                }}
              >
                Download template
              </a>
              <button
                type="button"
                style={{
                  ...styles.button,
                  width: "auto",
                  padding: "6px 10px",
                  marginBottom: "6px",
                }}
                onClick={() => batchFileInputRef.current?.click()}
              >
                Upload CSV
              </button>
              <WalletInlineButton
                type="button"
                style={{ marginBottom: 6 }}
                onClick={() => {
                  const first = sendBatchRows[0] ?? {
                    recipient: "",
                    amount: "",
                  };
                  setSendRecipient(first.recipient);
                  setSendAmount(first.amount);
                  setSendBatchMode(false);
                }}
              >
                Back to single
              </WalletInlineButton>
            </div>
          </div>
          <input
            ref={batchFileInputRef}
            type="file"
            accept=".csv,text/csv"
            style={{ display: "none" }}
            onChange={handleBatchFileUpload}
          />
          <WalletBatchRecipients
            rows={sendBatchRows}
            onChange={setSendBatchRows}
            placeholder="bob.stealth, MPK:IPK, or 0x recipient"
            disabled={!connected || loading}
            maxForRow={(index) => {
              try {
                const others = sendBatchRows.reduce(
                  (sum, row, i) =>
                    i === index || !row.amount.trim()
                      ? sum
                      : sum +
                        ethers.parseUnits(
                          row.amount,
                          selectedSendToken.decimals,
                        ),
                  0n,
                );
                if (
                  others < 0n ||
                  sendBatchRows.some(
                    (row, i) =>
                      i !== index && row.amount.trim().startsWith("-"),
                  )
                )
                  return null;
                let low = 0n,
                  high = spendablePrivateBalance;
                while (low < high) {
                  const mid = (low + high + 1n) / 2n;
                  if (mid + batchRelayFee(mid) <= spendablePrivateBalance)
                    low = mid;
                  else high = mid - 1n;
                }
                const remaining = low - others;
                return remaining > 0n
                  ? ethers.formatUnits(remaining, selectedSendToken.decimals)
                  : null;
              } catch {
                return null;
              }
            }}
          />
        </div>
      ) : (
        <>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "8px",
            }}
          >
            <label htmlFor="wallet-send-recipient" style={styles.label}>
              Recipient
            </label>
            {canUseBatchWithdraw && (
              <WalletInlineButton
                type="button"
                style={{ marginBottom: 6 }}
                onClick={enterBatchMode}
              >
                + Batch
              </WalletInlineButton>
            )}
          </div>
          <input
            style={styles.input}
            type="text"
            id="wallet-send-recipient"
            value={sendRecipient}
            onChange={(e) => setSendRecipient(e.target.value)}
            placeholder={
              chainAdapter?.recipientPlaceholder ??
              "bob.stealth, 0x... MPK (private), or EVM address"
            }
          />
          <label htmlFor="wallet-send-amount" style={styles.label}>
            Amount ({activeSendToken?.symbol ?? selectedSendToken.symbol})
          </label>
          <WalletAmountInput
            id="wallet-send-amount"
            value={sendAmount}
            aria-invalid={connected && !!validation.error}
            aria-describedby={
              connected && validation.error ? "send-amount-error" : undefined
            }
            onChange={(e) => setSendAmount(e.target.value)}
            placeholder="0.01"
            onMax={
              chainAdapter?.onMax ??
              (() => setSendAmount(sendPrivateBalanceDisplay))
            }
            disabled={!connected || loading}
            maxDisabled={spendablePrivateBalance <= 0n}
          />
          {connected && validation.error && (
            <p
              id="send-amount-error"
              role="status"
              style={{
                color: "#ff5263",
                fontSize: "13px",
                margin: "6px 0 12px",
              }}
            >
              {validation.error}
            </p>
          )}
        </>
      )}
      {!sendBatchMode && isStealthHandle(sendRecipient.trim()) && (
        <>
          <label htmlFor="wallet-send-memo" style={styles.label}>
            Memo (optional, private transfer only)
          </label>
          <input
            style={styles.input}
            type="text"
            id="wallet-send-memo"
            value={sendMemo}
            onChange={(e) => setSendMemo(e.target.value)}
            placeholder="thanks for dinner"
            disabled={sendBatchMode}
          />
        </>
      )}
      <div style={{ fontSize: "12px", color: "#b0c4de", marginBottom: "12px" }}>
        {chainAdapter ? (
          chainAdapter.feeSummary
        ) : usesPrivateFees() ? (
          <PrivateFeeSummary
            policy={privateFeePolicy ?? null}
            action={ethers.isAddress(sendRecipient.trim()) ? "withdraw" : "p2p"}
            amount={sendAmount}
          />
        ) : batchModeIsMixed ? (
          <div>Mixed batch recipient types are not allowed.</div>
        ) : batchModeIsPrivate ? (
          <div>Private batch transfers have no fee.</div>
        ) : withdrawalFee !== null && enteredAmount !== null ? (
          <WalletFeeSummary
            rows={[
              {
                label: "Relayer fee",
                value: `${formatTokenAmount(withdrawalFee, sendDecimals, "up")} ${sendSymbol}`,
              },
              {
                label: "Total fee",
                value: `${formatTokenAmount(withdrawalFee, sendDecimals, "up")} ${sendSymbol}`,
              },
            ]}
            received={
              enteredAmount > withdrawalFee
                ? `${formatTokenAmount(enteredAmount - withdrawalFee, sendDecimals, "down")} ${sendSymbol}`
                : undefined
            }
          />
        ) : sendBatchMode || sendFeesApply ? (
          <>
            <div>
              {EVM_CONFIG.JOINSPLIT_V2.ENABLED
                ? "Relayer fee"
                : `Protocol fee (${protocolFeeBps} bps)`}
              :{" "}
              {relayFeePending &&
              (sendBatchMode ? batchRelayFeeApplies : relayedWithdrawalForm)
                ? relayFeePending
                : `${(sendBatchMode
                    ? batchSendProtocolFee
                    : sendProtocolFee
                  ).toFixed(
                    6,
                  )} ${activeSendToken?.symbol ?? selectedSendToken.symbol}`}
            </div>
            {partnerFeeBps > 0 && (
              <div>
                Partner fee ({partnerFeeBps} bps):{" "}
                {(sendBatchMode ? batchSendPartnerFee : sendPartnerFee).toFixed(
                  6,
                )}{" "}
                {activeSendToken?.symbol ?? selectedSendToken.symbol}
              </div>
            )}
            <div>
              Total fee:{" "}
              {(sendBatchMode
                ? batchSendProtocolFee + batchSendPartnerFee
                : sendProtocolFee + sendPartnerFee
              ).toFixed(6)}{" "}
              {activeSendToken?.symbol ?? selectedSendToken.symbol}
            </div>
          </>
        ) : enteredAmount !== null &&
          enteredAmount > 0n &&
          !["erc721", "erc1155", "vnft"].includes(
            String(activeSendToken?.tokenType ?? ""),
          ) ? (
          <WalletFeeSummary
            rows={[]}
            received={`${formatTokenAmount(enteredAmount, sendDecimals, "down")} ${sendSymbol}`}
          >
            <div>Private transfers have no fee</div>
          </WalletFeeSummary>
        ) : (
          <div>Private transfers have no fee</div>
        )}
      </div>
      {activeSendToken?.isWrappedNft && (
        <div
          style={{ fontSize: "12px", color: "#f8d78c", marginBottom: "12px" }}
        >
          Wrapped NFT unwrap reveals underlying token identity on-chain at final
          withdrawal.
        </div>
      )}
      {!sendBatchMode && sendRecipient && (
        <p
          role="status"
          style={{
            color: recipientCheck.error ? "#ff5263" : "#b0c4de",
            fontSize: "13px",
          }}
        >
          {recipientCheck.message}
          {recipientCheck.error && isStealthHandle(sendRecipient) && (
            <button
              type="button"
              disabled={loading}
              onClick={recipientCheck.retry}
            >
              Check again
            </button>
          )}
        </p>
      )}
      {sendBatchMode && batchInvalid && (
        <p role="status" style={{ color: "#ff5263" }}>
          Check all recipient amounts and ensure the total fits your available
          balance.
        </p>
      )}
      {loading && (
        <BaseOperationProgress
          message={sendProgress || "Preparing transaction…"}
          extendedPreparationMessage="Processing your transaction…"
          completed={sendProgress === "Transaction complete"}
        />
      )}
      <button
        className="wallet-cta"
        style={{
          ...styles.button,
          ...styles.primaryButton,
          opacity: disabled ? 0.5 : 1,
          cursor: disabled ? "not-allowed" : "pointer",
        }}
        onClick={() => {
          if (!disabled) void onSend();
        }}
        disabled={disabled}
      >
        {loading ? "Working…" : (chainAdapter?.continueLabel ?? "Continue")}
      </button>
    </div>
  );
}
